/**
 * AddressTypeahead — typeahead input for the work-order Job Location field.
 *
 * Sources, combined into a single dropdown in this order:
 *   1. Association shortcut codes (when showShortcuts and associationId set)
 *   2. Accounts (units/lots) in the selected association, matched on account
 *      number, address, OR attached owner name — address matches ranked first
 *   3. Free-text address geocoding (always, and last)
 *
 * Account matching rides the global-search endpoint, so it is the same matching
 * Cmd+K does, narrowed to accounts in this association (#438).
 *
 * Selecting an account fills the Job Location with the LOT (shortcode or
 * address) and hands back the account id via onAccountSelect. The row may be
 * labelled by owner name, which is why the option carries an explicit
 * `fillValue` — the label must never reach the saved location.
 *
 * The field stays free-text first: anything typed that matches nothing is
 * committed verbatim, including several locations in one string.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { TypeaheadOption, TypeaheadSearch, type TypeaheadSearchHandle } from '@/components/search/typeahead-search';
import { useApi } from '@/hooks/use-api';
import { dataService } from '@/services/data-service';
import type { AssociationShortcutCode } from '@/types/models';
import { accountLotFillValue } from '@/utils/account-display';
import { rankLotMatchesFirst } from '@/utils/account-search-rank';
import { formatOwnerNames } from '@/utils/owner-name-label';
import { extractShortcode } from '@/utils/shortcode-lookup';

interface AddressAutocompleteInputProps {
	label?: string;
	value: string;
	onChange: (address: string) => void;
	onLocationChange?: (lat: number, lng: number) => void;
	onAccountSelect?: (accountId: string | undefined) => void;
	style?: any;
	disabled?: boolean;
	testID?: string;
	associationId?: string;
	showShortcuts?: boolean;
}

/**
 * Lets a form settle typed-but-uncommitted text before it validates. Free text here
 * commits on a blur that `TypeaheadSearch` defers by 200ms so a dropdown click wins the
 * race — meaning a Save clicked inside that window sees an empty field the user can
 * plainly see they filled in (#483 follow-up).
 */
export interface AddressTypeaheadHandle {
	/** Commit pending text and return the resulting address, or null if nothing changed. */
	flushPendingText: () => string | null;
}

export const AddressTypeahead = forwardRef<AddressTypeaheadHandle, AddressAutocompleteInputProps>(({
	label = 'Address',
	value,
	onChange,
	onLocationChange,
	onAccountSelect,
	disabled = false,
	testID,
	associationId,
	showShortcuts = true,
}, ref) => {
	const inner = useRef<TypeaheadSearchHandle>(null);
	const lastEmitted = useRef<string | null>(null);

	useImperativeHandle(ref, () => ({
		flushPendingText: () => {
			lastEmitted.current = null;
			inner.current?.flushPendingText();
			return lastEmitted.current;
		},
	}), []);
	const { searchAddress } = useApi();
	const [options, setOptions] = useState<TypeaheadOption[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const [selected, setSelected] = useState<TypeaheadOption | null>(null);

	useEffect(() => {
		if (value && (!selected || selected.label !== value)) {
			setSelected({ id: value, label: value });
		}
		if (!value && selected) {
			setSelected(null);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- selected is conditionally set
	}, [value]);

	const handleSearch = useCallback(async (text: string) => {
		const q = text.trim();
		if (q.length < 2) {
			setOptions([]);
			return;
		}
		setLoading(true);
		setError(undefined);
		const all: TypeaheadOption[] = [];
		try {
			// Shortcut codes (e.g., "742PS")
			if (showShortcuts) {
				try {
					const codes = await dataService.searchShortcutCodes(q, associationId);
					for (const c of codes as AssociationShortcutCode[]) {
						all.push({
							id: `shortcut-${c.id}`,
							label: c.shortcutCode,
							description: c.fullAddress,
							lat: c.latitude || 0,
							lng: c.longitude || 0,
							type: 'shortcut',
							shortcutCode: c.shortcutCode,
							associationId: c.associationId,
						});
					}
				} catch (err) { console.warn('shortcut search failed:', err); }
			}

			// Accounts in this association, matched server-side so owner names come
			// along and no row cap silently truncates a large association. We only
			// search when an association is picked; otherwise the result set would
			// span every association the user can see.
			if (associationId) {
				try {
					const accounts = await dataService.searchAccounts(q, { associationId });
					for (const a of rankLotMatchesFirst(accounts, q)) {
						const shortcode = extractShortcode(a.address) ?? a.accountNumber;
						// Never the owner name: this is what lands in the field.
						const lot = accountLotFillValue(a);
						const owners = formatOwnerNames(a.ownerNames);
						all.push({
							id: `account-${a.id}`,
							label: owners || lot || a.id.slice(0, 8),
							description: [shortcode, a.address].filter(Boolean).join(' · ')
								|| `Account ${a.accountNumber ?? a.id.slice(0, 8)}`,
							type: 'account',
							accountId: a.id,
							associationId: a.associationId,
							shortcutCode: shortcode || undefined,
							fillValue: lot || undefined,
						});
					}
				} catch (err) { console.warn('account search failed:', err); }
			}

			// Free-text address geocoding
			try {
				const addrs = await searchAddress(q);
				for (const r of addrs) {
					all.push({
						id: r.id,
						label: r.label,
						description: r.label,
						lat: r.lat,
						lng: r.lng,
						type: 'address',
					});
				}
			} catch (err) { console.warn('address geocoding failed:', err); }

			setOptions(all);
		} catch {
			setError('Could not fetch suggestions');
			setOptions([]);
		} finally {
			setLoading(false);
		}
	}, [searchAddress, showShortcuts, associationId]);

	const handleSelect = (option: TypeaheadOption | null) => {
		setSelected(option);
		// Mirror whatever we hand the parent, so `flushPendingText` can return the value
		// synchronously — `onChange` only schedules the parent's state update, which a
		// caller validating in the same tick would otherwise miss entirely.
		const emit = (address: string) => {
			lastEmitted.current = address;
			onChange(address);
		};
		if (!option) {
			emit('');
			onAccountSelect?.(undefined);
			return;
		}
		if (option.type === 'shortcut' && option.shortcutCode) {
			emit(option.shortcutCode);
			if (onLocationChange && option.lat && option.lng) onLocationChange(option.lat, option.lng);
			onAccountSelect?.(undefined);
		} else if (option.type === 'account') {
			// fillValue, never label — the label may be a person's name.
			emit(option.fillValue || option.shortcutCode || '');
			onAccountSelect?.(option.accountId);
		} else {
			emit(option.label);
			if (onLocationChange && option.lat && option.lng) onLocationChange(option.lat, option.lng);
			onAccountSelect?.(undefined);
		}
	};

	return (
		<TypeaheadSearch
			ref={inner}
			label={label}
			placeholder="Enter address..."
			onSearch={handleSearch}
			onSelect={handleSelect}
			options={options}
			loading={loading}
			error={error}
			selectedOption={selected}
			testID={testID}
			allowCreate={!disabled}
			// Addresses are free-text fills — committing on blur never creates
			// anything, so tab/click-away keeping the typed address is safe.
			commitOnBlur={!disabled}
			// "Community" is a real job location. Tab must not swap it for whichever
			// lone suggestion happens to be showing.
			tabCompleteLoneOption={false}
		/>
	);
});

AddressTypeahead.displayName = 'AddressTypeahead';
